<template>
  <div class="signin-shell">
    <!-- Left: Form panel -->
    <div class="signin-form-panel">
      <div class="signin-form-inner">
        <!-- Logo -->
        <div class="signin-logo">
          <div class="signin-logo__icon">
            <i class="fas fa-building" style="font-size:1.1rem; color:#fff"></i>
          </div>
          <span class="signin-logo__text">CRMRED</span>
        </div>

        <!-- Heading -->
        <div class="signin-heading">
          <h1 class="signin-heading__title">Bienvenido de nuevo</h1>
          <p class="signin-heading__sub">Ingresa tus credenciales para continuar</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleLogin" class="signin-form" novalidate>
          <!-- Email -->
          <div class="form-field">
            <label class="form-field__label" for="email">Correo electrónico</label>
            <div class="form-field__input-wrap">
              <i class="fas fa-envelope form-field__icon"></i>
              <input
                id="email"
                type="email"
                class="form-field__input"
                :class="{ 'form-field__input--error': emailError }"
                placeholder="correo@empresa.com"
                v-model="email"
                autocomplete="email"
              />
            </div>
            <p v-if="emailError" class="form-field__error">{{ emailError }}</p>
          </div>

          <!-- Password -->
          <div class="form-field">
            <label class="form-field__label" for="password">Contraseña</label>
            <div class="form-field__input-wrap">
              <i class="fas fa-lock form-field__icon"></i>
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                class="form-field__input"
                :class="{ 'form-field__input--error': passwordError }"
                placeholder="••••••••"
                v-model="password"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="form-field__toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <p v-if="passwordError" class="form-field__error">{{ passwordError }}</p>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            class="signin-submit"
            :disabled="isLoading"
          >
            <span v-if="isLoading">
              <i class="fas fa-spinner fa-spin me-2"></i>Iniciando sesión...
            </span>
            <span v-else>
              Iniciar sesión
              <i class="fas fa-arrow-right ms-2"></i>
            </span>
          </button>
        </form>
      </div>
    </div>

    <!-- Right: Brand panel -->
    <div class="signin-brand-panel">
      <div class="signin-brand-inner">
        <div class="signin-brand-logo">
          <i class="fas fa-building"></i>
        </div>
        <h2 class="signin-brand-title">CRM Red</h2>
        <p class="signin-brand-desc">
          Plataforma de gestión inmobiliaria para profesionales del sector.
          Administra membresías, leads, lonjas y más desde un solo lugar.
        </p>
        <div class="signin-brand-features">
          <div class="signin-brand-feature">
            <i class="fas fa-check-circle"></i>
            <span>Gestión de membresías</span>
          </div>
          <div class="signin-brand-feature">
            <i class="fas fa-check-circle"></i>
            <span>Control de leads y PQR</span>
          </div>
          <div class="signin-brand-feature">
            <i class="fas fa-check-circle"></i>
            <span>Integraciones con portales</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import ROUTES from '../../constansRoute.js'

export default {
  name: 'SignIn',
  setup() {
    const store = useStore()
    const router = useRouter()
    const isLoading = ref(false)
    const showPassword = ref(false)

    const email = ref('')
    const emailError = ref('')
    const password = ref('')
    const passwordError = ref('')

    const toggleEveryDisplay = () => store.commit('appTemplate/toggleEveryDisplay')
    const toggleHideConfig = () => store.commit('appTemplate/toggleHideConfig')

    const emailIsValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

    watch(email, (v) => {
      if (!v) { emailError.value = ''; return }
      emailError.value = emailIsValid(v) ? '' : 'Correo electrónico inválido'
    })

    watch(password, (v) => {
      if (!v) { passwordError.value = ''; return }
      passwordError.value = v.length < 5 ? 'Mínimo 5 caracteres' : ''
    })

    const hasErrors = computed(() => !!emailError.value || !!passwordError.value)

    const handleLogin = async () => {
      if (!email.value || !password.value) return
      if (hasErrors.value) return
      isLoading.value = true
      try {
        await store.dispatch('appAuth/login', { email: email.value, password: password.value })
        await store.dispatch('appUserdata/getUserdata')
        router.push({ name: ROUTES.MEMBERSHIPS })
      } catch (e) {
        console.error(e)
      } finally {
        isLoading.value = false
      }
    }

    toggleEveryDisplay()
    toggleHideConfig()

    onBeforeUnmount(() => {
      toggleEveryDisplay()
      toggleHideConfig()
    })

    return { email, emailError, password, passwordError, isLoading, showPassword, handleLogin }
  },
}
</script>

<style scoped>
/* Shell: two-column layout */
.signin-shell {
  display: flex;
  min-height: 100vh;
  width: 100vw;
  font-family: 'Inter', sans-serif;
}

/* ── Form panel (left) ─────────────────────────────────── */
.signin-form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  padding: 40px 24px;
  min-height: 100vh;
}

.signin-form-inner {
  width: 100%;
  max-width: 400px;
}

/* Logo */
.signin-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 40px;
}

.signin-logo__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1E3A5F, #2563EB);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(37,99,235,0.3);
}

.signin-logo__text {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1E293B;
  letter-spacing: 0.05em;
}

/* Heading */
.signin-heading { margin-bottom: 32px; }
.signin-heading__title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 6px;
  line-height: 1.2;
}
.signin-heading__sub {
  font-size: 0.85rem;
  color: #64748B;
  margin: 0;
}

/* Form fields */
.signin-form { display: flex; flex-direction: column; gap: 20px; }

.form-field { display: flex; flex-direction: column; gap: 6px; }

.form-field__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}

.form-field__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-field__icon {
  position: absolute;
  left: 12px;
  color: #94A3B8;
  font-size: 0.85rem;
  pointer-events: none;
}

.form-field__input {
  width: 100%;
  padding: 11px 40px 11px 38px;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  font-size: 0.85rem;
  color: #1E293B;
  background: #FAFAFA;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  font-family: 'Inter', sans-serif;
}

.form-field__input:focus {
  border-color: #2563EB;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}

.form-field__input--error {
  border-color: #EF4444;
}

.form-field__toggle {
  position: absolute;
  right: 12px;
  border: none;
  background: transparent;
  color: #94A3B8;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  transition: color 0.15s ease;
}
.form-field__toggle:hover { color: #475569; }

.form-field__error {
  font-size: 0.72rem;
  color: #EF4444;
  margin: 0;
}

/* Submit button */
.signin-submit {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #1E3A5F, #2563EB);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  box-shadow: 0 4px 14px rgba(37,99,235,0.3);
  margin-top: 4px;
}
.signin-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(37,99,235,0.4);
}
.signin-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* ── Brand panel (right) ───────────────────────────────── */
.signin-brand-panel {
  width: 420px;
  flex-shrink: 0;
  background: linear-gradient(160deg, #0F172A 0%, #1E3A5F 50%, #1D4ED8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  position: relative;
  overflow: hidden;
}

.signin-brand-panel::before {
  content: '';
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(37,99,235,0.15);
  top: -80px;
  right: -80px;
}

.signin-brand-panel::after {
  content: '';
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255,255,255,0.04);
  bottom: -60px;
  left: -40px;
}

.signin-brand-inner {
  position: relative;
  z-index: 1;
  color: #fff;
  text-align: center;
}

.signin-brand-logo {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #fff;
  margin: 0 auto 20px;
}

.signin-brand-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #fff;
  margin: 0 0 12px;
  letter-spacing: 0.02em;
}

.signin-brand-desc {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.65);
  line-height: 1.6;
  margin: 0 0 32px;
}

.signin-brand-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.signin-brand-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.83rem;
  color: rgba(255,255,255,0.8);
}

.signin-brand-feature i {
  color: #34D399;
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .signin-brand-panel { display: none; }
}
</style>
